import React, { useState } from "react";
import { CheckCircle, XCircle } from "lucide-react";

/**
 * A reusable modal component for approving or rejecting requests.
 *
 * @param {boolean} isOpen - Whether the modal is visible.
 * @param {string} actionType - The type of action: "approved" or "rejected".
 * @param {string} employeeName - The name of the employee submitting the request.
 * @param {function} onClose - Callback function to close the modal.
 * @param {function} onConfirm - Callback function when the user confirms the action. Receives the approver's comments as an argument.
 */
const ApproveRejectModal = ({
  isOpen,
  actionType,
  employeeName,
  onClose,
  onConfirm,
}) => {
  const [comments, setComments] = useState("");

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(comments);
    setComments(""); // Reset comments after confirm
  };

  const handleClose = () => {
    onClose();
    setComments(""); // Reset comments on close
  };

  return (
    <div
      className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[20000] p-4 animate-in fade-in duration-300"
      onClick={handleClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-300 font-poppins border dark:border-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center gap-3 font-poppins text-left">
          <div
            className={`p-2 rounded-full ${
              actionType === "approved"
                ? "bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400"
                : "bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400"
            }`}
          >
            {actionType === "approved" ? (
              <CheckCircle className="w-6 h-6" />
            ) : (
              <XCircle className="w-6 h-6" />
            )}
          </div>
          <div className="text-left">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white leading-tight font-poppins">
              {actionType === "approved" ? "Approve Request" : "Reject Request"}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium font-poppins tracking-wide">
              {employeeName || "Employee"}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 text-left">
          <label
            htmlFor="approver-comments"
            className="flex text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tracking-wider font-poppins"
          >
            Manager Remark
          </label>
          <div className="relative group">
            <textarea
              id="approver-comments"
              rows={4}
              className="w-full p-4 text-base text-gray-700 dark:text-white border-[2px] border-gray-100 dark:border-gray-700 dark:bg-gray-800/50 rounded-lg focus:outline-none focus:border-gray-400 transition-all resize-none placeholder:text-gray-400 font-poppins"
              placeholder="Write your reason or feedback here..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
          <p className="mt-2 text-[10px] italic text-gray-400 dark:text-gray-500 font-medium text-left">
            * This remark will be visible to the employee.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50/50 dark:bg-gray-800/30 flex items-center justify-end gap-3 border-t dark:border-gray-700 font-poppins">
          <button
            onClick={handleClose}
            className="px-6 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all font-poppins uppercase tracking-wide"
          >
            CANCEL
          </button>
          <button
            onClick={handleConfirm}
            className={`px-8 py-2.5 text-sm font-semibold text-white rounded-lg transition-all transform hover:scale-105 active:scale-95 uppercase tracking-wide font-poppins ${
              actionType === "approved"
                ? "bg-green-600 hover:bg-green-700 shadow-green-200 dark:shadow-none"
                : "bg-red-600 hover:bg-red-700 shadow-red-200 dark:shadow-none"
            }`}
          >
            {actionType === "approved" ? "APPROVE" : "REJECT"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApproveRejectModal;
